"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { FollowUpMessage } from "../types";

interface FollowUpPanelProps {
  messages: FollowUpMessage[];
  isLoading: boolean;
  error: string | null;
  onSend: (question: string) => void;
  isOpen: boolean;
  onToggle: () => void;
}

export default function FollowUpPanel({
  messages,
  isLoading,
  error,
  onSend,
  isOpen,
  onToggle,
}: FollowUpPanelProps) {
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isLoading, isOpen, scrollToBottom]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;
    setInputValue("");
    onSend(trimmed);
  }, [inputValue, isLoading, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-zinc-800/30 transition-colors duration-150"
      >
        <div className="flex items-center gap-2.5">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-violet-400">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <span className="text-sm font-semibold text-violet-400">Ask a follow-up</span>
          {messages.length > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-400 border border-violet-500/25">
              {messages.filter((m) => m.role === "user").length}
            </span>
          )}
        </div>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`text-zinc-600 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="border-t border-zinc-800">
          <div className="max-h-80 overflow-y-auto px-5 py-4 space-y-4">
            {messages.length === 0 && !isLoading && (
              <div className="text-center py-6">
                <p className="text-zinc-600 text-sm">
                  Ask a question about this explanation...
                </p>
                <div className="flex justify-center gap-2 mt-3">
                  {[
                    "Can you explain step 2 more?",
                    "What if I'm using a different version?",
                    "How do I prevent this in the future?",
                  ].map((hint) => (
                    <button
                      key={hint}
                      onClick={() => {
                        setInputValue(hint);
                        inputRef.current?.focus();
                      }}
                      className="text-xs px-2.5 py-1.5 rounded-lg bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600 hover:text-zinc-300 transition-colors text-left max-w-[200px]"
                    >
                      {hint}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm leading-6 ${
                    msg.role === "user"
                      ? "bg-cyan-500/15 text-cyan-200 border border-cyan-500/25"
                      : "bg-zinc-800 text-zinc-300 border border-zinc-700"
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 flex items-center gap-2">
                  <span className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-pulse" style={{ animationDelay: "0.15s" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-pulse" style={{ animationDelay: "0.3s" }} />
                  </span>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-950/40 border border-red-500/30 rounded-xl p-3 flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-400 flex-shrink-0">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p className="text-red-300 text-xs">{error}</p>
                <button
                  onClick={() => onSend(inputValue || "Try again")}
                  disabled={isLoading}
                  className="ml-auto text-xs text-red-400 hover:text-red-300 underline transition-colors"
                >
                  Retry
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="border-t border-zinc-800 px-4 py-3 flex items-end gap-2.5">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a follow-up question..."
              rows={1}
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2.5 text-sm text-zinc-200 placeholder-zinc-500 resize-none focus:outline-none focus:border-violet-500/50 transition-colors"
              disabled={isLoading}
              maxLength={2000}
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !inputValue.trim()}
              className="flex-shrink-0 p-2.5 rounded-xl bg-violet-500/20 text-violet-400 border border-violet-500/25 hover:bg-violet-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
